import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Target,
  Building2,
  Cog,
  Database,
  Users,
  Shield,
  GitBranch,
  Compass,
  BarChart3,
  Settings,
  Lightbulb,
  Rocket,
  UserCheck,
  ShieldCheck,
  ArrowRight,
  Check,
  ChevronRight,
  Briefcase,
  TrendingUp,
  Award,
  X,
  Send,
  Loader,
  MessageCircle
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= DEMO REQUEST MODAL =============
const DemoRequestModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    teamSize: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.role) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          interest: 'Corporate AI Training - Demo Request',
          message: `Corporate AI Training Demo Request\n\nCompany: ${formData.company}\nRole: ${formData.role}\nTeam Size: ${formData.teamSize || 'Not specified'}\n\nAdditional Message:\n${formData.message || 'No additional message'}\n\nPlease contact me to schedule a demo.`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Demo request submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', company: '', role: '', teamSize: '', message: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Demo request error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', company: '', role: '', teamSize: '', message: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-950 backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/30">
              <Check size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Request Submitted!</h3>
            <p className="text-gray-400 mb-6">
              Thank you for your interest in Corporate AI Training. Our enterprise team will contact you within 24 hours to schedule your demo.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
              <Briefcase size={14} className="text-teal-400" />
              <span className="text-xs text-teal-400 font-semibold">Enterprise Program</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Request Demo</h2>
            <p className="text-teal-400 mb-6">Corporate AI Training - 14-Day Program</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Work Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your work email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Company Name *</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your company name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Your Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select your role</option>
                  <option value="C-Suite Executive (CEO, CTO, CDO)" className="bg-slate-950">C-Suite Executive (CEO, CTO, CDO)</option>
                  <option value="VP / Director" className="bg-slate-950">VP / Director</option>
                  <option value="Manager / Team Lead" className="bg-slate-950">Manager / Team Lead</option>
                  <option value="Strategy / Planning" className="bg-slate-950">Strategy / Planning</option>
                  <option value="HR / L&D" className="bg-slate-950">HR / L&D</option>
                  <option value="Other" className="bg-slate-950">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Team Size</label>
                <select
                  value={formData.teamSize}
                  onChange={(e) => handleChange('teamSize', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select team size</option>
                  <option value="1-10 employees" className="bg-slate-950">1-10 employees</option>
                  <option value="11-50 employees" className="bg-slate-950">11-50 employees</option>
                  <option value="51-200 employees" className="bg-slate-950">51-200 employees</option>
                  <option value="201-500 employees" className="bg-slate-950">201-500 employees</option>
                  <option value="500+ employees" className="bg-slate-950">500+ employees</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Message (Optional)</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition resize-none"
                  placeholder="Tell us about your AI transformation goals..."
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Request Demo
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= NAVBAR =============
const Navbar = ({ onRequestDemo }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/80 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </Link>

          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm text-gray-300 hover:text-teal-400 transition">Home</Link>
            <a href="#curriculum" className="text-sm text-gray-300 hover:text-teal-400 transition">Curriculum</a>
            <a href="#benefits" className="text-sm text-gray-300 hover:text-teal-400 transition">Benefits</a>
            <a href="#journey" className="text-sm text-gray-300 hover:text-teal-400 transition">Journey</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onRequestDemo}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Request Demo
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-80 h-80 bg-blue-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-80 h-80 bg-emerald-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-80 h-80 bg-teal-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = ({ onRequestDemo }) => {
  const stats = [
    { number: "8", label: "Strategic Modules" },
    { number: "40+", label: "Implementation Cases" },
    { number: "10X", label: "ROI Potential" },
    { number: "C-Suite", label: "Certification Level" }
  ];

  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden pt-20 bg-slate-950">
      <GradientOrbs />

      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.15),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Badge */}
        <motion.div
          className="mb-6 inline-flex items-center gap-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <span className="text-xs font-semibold text-blue-300 px-4 py-2 rounded-full border border-blue-500/50 bg-blue-500/10 backdrop-blur-sm flex items-center gap-2">
            <Briefcase size={14} />
            FOR PROFESSIONALS
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="block bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Corporate AI Training
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-teal-300 mb-4 font-medium"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Executive AI Integration Protocol
        </motion.p>

        {/* Description */}
        <motion.p
          className="text-lg text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Advanced AI transformation framework for enterprise professionals. Deploy cutting-edge artificial intelligence strategies to revolutionize your organization's competitive advantage, operational efficiency, and market positioning in the digital economy.
        </motion.p>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 hover:border-teal-500/30 transition-all duration-300"
              whileHover={{ y: -4, scale: 1.02 }}
            >
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent mb-1">
                {stat.number}
              </div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(20, 184, 166, 0.3)" }}
            whileTap={{ scale: 0.95 }}
            onClick={onRequestDemo}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold shadow-[0_0_30px_rgba(20,184,166,0.2)] transition flex items-center gap-2"
          >
            Begin Transformation <ArrowRight size={18} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('curriculum').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-500/40"
          >
            View Curriculum
          </motion.button>
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= MODULE CARD =============
const ModuleCard = ({ module, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-50px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-emerald-500/10 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 hover:border-teal-500/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-teal-500/50 to-transparent" />

        <div className="relative z-10 flex flex-col h-full">
          <div className="mb-4 inline-flex items-center gap-2">
            <span className="text-xs font-bold text-teal-400 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20">
              Module {String(index + 1).padStart(2, '0')}
            </span>
          </div>

          <motion.div
            className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/20 group-hover:from-blue-500/30 group-hover:to-emerald-500/30 transition-all duration-500"
            animate={isHovered ? { rotate: [0, -10, 10, -10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <module.icon className="text-teal-300 group-hover:text-teal-200 transition" size={28} />
          </motion.div>

          <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all duration-300">
            {module.title}
          </h3>

          <p className="text-gray-400 mb-6 leading-relaxed text-sm flex-grow">
            {module.description}
          </p>

          <ul className="space-y-2">
            {module.topics.map((topic, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-500 flex items-start gap-2"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i }}
              >
                <Check size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>{topic}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============= CURRICULUM SECTION =============
const CurriculumSection = () => {
  const modules = [
    {
      icon: Target,
      title: "Strategic AI Vision",
      description: "Develop executive-level understanding of AI's business impact, market disruption patterns, and strategic implementation frameworks.",
      topics: [
        "AI business case development & ROI modeling",
        "Industry disruption analysis & competitive intelligence",
        "Enterprise AI architecture & governance",
        "Digital transformation roadmap design"
      ]
    },
    {
      icon: Building2,
      title: "Industry Solutions",
      description: "Master sector-specific AI applications and deployment strategies across diverse business verticals and use cases.",
      topics: [
        "Financial services: fraud detection & risk management",
        "Healthcare: diagnostics & patient care optimization",
        "Manufacturing: predictive maintenance & quality control",
        "Retail: personalization & demand forecasting"
      ]
    },
    {
      icon: Cog,
      title: "Operational Excellence",
      description: "Implement AI-driven process optimization, workflow automation, and operational efficiency improvements at enterprise scale.",
      topics: [
        "Intelligent process automation (IPA) deployment",
        "Supply chain optimization with AI analytics",
        "Customer service transformation & chatbot strategy",
        "Document processing & knowledge management"
      ]
    },
    {
      icon: Database,
      title: "Data Strategy",
      description: "Build robust data infrastructure, governance frameworks, and analytics capabilities to power AI initiatives.",
      topics: [
        "Enterprise data architecture & lake design",
        "Data quality management & cleansing protocols",
        "Advanced analytics & business intelligence integration",
        "Real-time data pipelines & processing systems"
      ]
    },
    {
      icon: Users,
      title: "AI Teams & Culture",
      description: "Structure high-performing AI teams, manage talent acquisition, and foster innovation culture within your organization.",
      topics: [
        "AI talent strategy & recruitment frameworks",
        "Cross-functional team structure & collaboration",
        "Upskilling programs & capability development",
        "Innovation labs & center of excellence setup"
      ]
    },
    {
      icon: Shield,
      title: "Risk & Compliance",
      description: "Navigate regulatory landscape, implement ethical AI practices, and establish comprehensive risk management protocols.",
      topics: [
        "AI governance frameworks & ethical guidelines",
        "Regulatory compliance (GDPR, CCPA, AI Act)",
        "Bias detection & fairness monitoring systems",
        "Security protocols & adversarial defense"
      ]
    },
    {
      icon: GitBranch,
      title: "Change Management",
      description: "Lead organizational transformation, manage stakeholder alignment, and drive adoption of AI initiatives across enterprise.",
      topics: [
        "Executive sponsorship & board communication",
        "Change readiness assessment & gap analysis",
        "Stakeholder engagement & adoption strategies",
        "Performance measurement & KPI frameworks"
      ]
    },
    {
      icon: Compass,
      title: "Future Roadmap",
      description: "Prepare for emerging AI technologies, anticipate market evolution, and position organization for long-term success.",
      topics: [
        "Generative AI & large language model strategy",
        "Quantum computing readiness & applications",
        "AI-first business model innovation",
        "Sustainable competitive advantage frameworks"
      ]
    }
  ];

  return (
    <section id="curriculum" className="relative py-24 px-4 overflow-hidden bg-slate-950">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[300px] bg-gradient-to-b from-teal-950/20 to-transparent" />
      <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-blue-500/[0.03] rounded-full blur-[120px]" />
      <div className="absolute bottom-40 left-0 w-[300px] h-[300px] bg-emerald-600/[0.03] rounded-full blur-[100px]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" />
            Executive Curriculum
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Enterprise AI{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">Mastery Framework</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            Comprehensive transformation roadmap designed for business leaders, executives, and decision-makers 
            seeking to leverage AI for sustainable competitive advantage and organizational excellence.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {modules.map((module, index) => (
            <ModuleCard key={index} module={module} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= BENEFITS SECTION =============
const BenefitsSection = () => {
  const benefits = [
    {
      icon: BarChart3,
      title: "Revenue Acceleration",
      description: "Deploy AI-powered sales intelligence, dynamic pricing optimization, and predictive analytics to unlock new revenue streams and accelerate growth trajectories across business units."
    },
    {
      icon: Settings,
      title: "Cost Optimization",
      description: "Implement intelligent automation and process optimization to reduce operational expenses by 30-50% while improving service quality and employee satisfaction metrics."
    },
    {
      icon: Lightbulb,
      title: "Decision Intelligence",
      description: "Leverage advanced analytics and AI-powered insights for data-driven decision making, risk mitigation, and strategic planning at executive and operational levels."
    },
    {
      icon: Rocket,
      title: "Innovation Velocity",
      description: "Accelerate product development cycles, reduce time-to-market, and foster continuous innovation culture through AI-augmented R&D and rapid prototyping capabilities."
    },
    {
      icon: UserCheck,
      title: "Customer Experience",
      description: "Transform customer interactions with personalized experiences, predictive service, and omnichannel engagement powered by AI-driven insights and automation."
    },
    {
      icon: ShieldCheck,
      title: "Risk Mitigation",
      description: "Deploy advanced threat detection, compliance monitoring, and predictive risk management systems to protect assets, reputation, and ensure regulatory adherence."
    }
  ];

  return (
    <section id="benefits" className="relative py-24 px-4 overflow-hidden bg-slate-900/50">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(20,184,166,0.05),transparent_70%)]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" />
            Strategic Advantages
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Enterprise Transformation{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">Value</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            Corporate AI Training delivers measurable business impact through strategic AI integration, 
            operational excellence, and sustainable competitive differentiation in rapidly evolving markets.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 hover:border-teal-500/30 transition-all duration-300 h-full">
                <motion.div
                  className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/20 group-hover:from-blue-500/30 group-hover:to-emerald-500/30 transition-all"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <benefit.icon className="text-teal-300 group-hover:text-teal-200 transition" size={26} />
                </motion.div>

                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-teal-400 transition-all">
                  {benefit.title}
                </h3>

                <p className="text-gray-400 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= JOURNEY SECTION (Timeline) =============
const JourneySection = () => {
  const stages = [
    {
      title: "Foundation Phase",
      description: "Establish AI vision, secure executive sponsorship, assess organizational readiness, and develop comprehensive strategy aligned with business objectives and market positioning."
    },
    {
      title: "Pilot Deployment",
      description: "Launch targeted proof-of-concept initiatives, validate business cases, demonstrate ROI, and build organizational confidence through quick wins and measurable success metrics."
    },
    {
      title: "Scale Integration",
      description: "Expand successful pilots across departments, implement enterprise-wide data infrastructure, establish governance frameworks, and drive adoption through change management programs."
    },
    {
      title: "Optimization Excellence",
      description: "Achieve full AI integration maturity with continuous improvement cycles, advanced capability development, innovation ecosystem, and sustained competitive advantage in digital marketplace."
    }
  ];

  return (
    <section id="journey" className="relative py-24 px-4 overflow-hidden bg-slate-950">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-teal-500/[0.03] rounded-full blur-[150px]" />

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" />
            Implementation Path
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Enterprise AI{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">Maturity Journey</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Structured progression from AI strategy formulation to full-scale enterprise deployment, 
            ensuring sustainable transformation and measurable business outcomes at each milestone.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-8 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-blue-500 via-teal-500 to-emerald-500" />

          {stages.map((stage, index) => (
            <motion.div
              key={index}
              className={`relative flex items-start gap-8 mb-12 last:mb-0 ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              }`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 border-4 border-slate-950 z-10" />

              <div className={`ml-16 md:ml-0 md:w-[calc(50%-2rem)] ${index % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8'}`}>
                <motion.div
                  className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300"
                  whileHover={{ y: -4 }}
                >
                  <div className={`mb-3 ${index % 2 === 0 ? 'md:flex md:justify-end' : ''}`}>
                    <span className="text-xs font-bold text-teal-400 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20">
                      Phase {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">
                    {stage.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {stage.description}
                  </p>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= TARGET AUDIENCE SECTION =============
const TargetAudienceSection = () => {
  const audiences = [
    { emoji: "👔", title: "C-Suite Executives", desc: "CEOs, CTOs, CDOs driving digital transformation" },
    { emoji: "📊", title: "Business Leaders", desc: "VPs and Directors seeking AI-powered growth" },
    { emoji: "🎯", title: "Strategy Teams", desc: "Teams developing AI implementation roadmaps" },
    { emoji: "🏢", title: "Enterprise Teams", desc: "Cross-functional groups adopting AI at scale" }
  ];

  return (
    <section className="relative py-20 px-4 overflow-hidden bg-slate-900/50">
      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Designed For Enterprise Leaders
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {audiences.map((audience, index) => (
            <motion.div
              key={index}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 text-center hover:border-teal-500/30 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
            >
              <div className="text-4xl mb-3">{audience.emoji}</div>
              <h3 className="text-white font-semibold mb-2">{audience.title}</h3>
              <p className="text-gray-500 text-sm">{audience.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onRequestDemo }) => {
  return (
    <section className="relative py-28 px-6 overflow-hidden bg-slate-950">
      <div className="max-w-3xl mx-auto">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[300px] bg-gradient-to-r from-blue-500/10 via-teal-500/10 to-emerald-500/10 rounded-full blur-[100px]" />
        </div>

        <motion.div
          className="relative text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/20"
            whileHover={{ scale: 1.1, rotate: 5 }}
          >
            <TrendingUp className="text-teal-300" size={40} />
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 tracking-tight">
            Transform Your{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">Organization</span>
          </h2>
          <p className="text-gray-400 mb-6 max-w-lg mx-auto leading-relaxed">
            Transform your organization with enterprise-grade AI strategy. Join leading executives driving digital transformation and competitive excellence.
          </p>

          {/* What's Included */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {["Executive Certification", "Real Case Studies", "ROI Frameworks", "Ongoing Support"].map((item, i) => (
              <span key={i} className="text-sm text-gray-400 flex items-center gap-2">
                <Check size={16} className="text-emerald-400" />
                {item}
              </span>
            ))}
          </div>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(20, 184, 166, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              onClick={onRequestDemo}
              className="px-10 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 text-white rounded-full font-semibold text-lg hover:from-blue-400 hover:via-teal-400 hover:to-emerald-400 transition-all flex items-center justify-center gap-2"
            >
              Begin Transformation <ArrowRight size={20} />
            </motion.button>
            <Link to="/training">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 border border-white/20 rounded-full text-white font-semibold text-lg hover:bg-white/10 hover:border-teal-500/40 transition-all"
              >
                View All Programs
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/5 py-12 px-4 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
              AI Factory
            </Link>
            <p className="text-gray-400 text-sm mt-4">Enterprise AI transformation for industry leaders.</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Programs</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/ai-fundamentals" className="hover:text-teal-400 transition">AI Fundamentals</Link></li>
              <li><Link to="/corporate-ai-training" className="hover:text-teal-400 transition">Corporate Training</Link></li>
              <li><Link to="/ai-fellowship" className="hover:text-teal-400 transition">AI Fellowship</Link></li>
              <li><Link to="/student-program" className="hover:text-teal-400 transition">Student Program</Link></li>
              <li><Link to="/prompt-engineering" className="hover:text-teal-400 transition">Prompt Engineering</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/#about" className="hover:text-teal-400 transition">About</Link></li>
              <li><Link to="/training" className="hover:text-teal-400 transition">All Programs</Link></li>
              <li><Link to="/#contact" className="hover:text-teal-400 transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-teal-400 transition">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Terms of Service</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Refund Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-600 text-sm">© 2025 AI Factory. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN PAGE =============
export default function CorporateAITrainingPage() {
  const [demoModal, setDemoModal] = useState(false);

  const handleRequestDemo = () => {
    setDemoModal(true);
  };

  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar onRequestDemo={handleRequestDemo} />
      <Hero onRequestDemo={handleRequestDemo} />
      <CurriculumSection />
      <BenefitsSection />
      <TargetAudienceSection />
      <JourneySection />
      <FinalCTA onRequestDemo={handleRequestDemo} />
      <Footer />

      {/* Demo Request Modal */}
      <DemoRequestModal
        isOpen={demoModal}
        onClose={() => setDemoModal(false)}
      />
    </div>
  );
}